import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Mail, Send, Users, Plus, Eye, Trash2, Clock, CheckCircle, 
  RefreshCw, Upload, UserPlus, Tag, X
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align', 'link', 'image'
];

export default function AgentNewsletters() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("newsletters");
  
  // Newsletter state
  const [newsletters, setNewsletters] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    subject: "",
    content: "",
    audience: "all",
    custom_user_ids: []
  });

  // Subscriber state
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberStats, setSubscriberStats] = useState(null);
  const [subscriberDialogOpen, setSubscriberDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [subscriberForm, setSubscriberForm] = useState({ email: "", name: "", tags: [] });
  const [newTag, setNewTag] = useState("");
  const [csvData, setCsvData] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [newslettersRes, audiencesRes, subscribersRes, statsRes] = await Promise.all([
        axios.get(`${API}/agent/newsletters`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/newsletters/audiences/list`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/newsletter-subscribers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/newsletter-subscribers/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setNewsletters(newslettersRes.data.newsletters || []);
      setAudiences(audiencesRes.data.audiences || []);
      setSubscribers(subscribersRes.data.subscribers || []);
      setSubscriberStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Newsletter handlers
  const handleCreateNewsletter = async () => {
    if (!form.subject || !form.content) {
      toast.error("Subject and content are required");
      return;
    }
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/newsletters`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Newsletter created");
      setDialogOpen(false);
      setForm({ subject: "", content: "", audience: "all", custom_user_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create newsletter");
    }
  };

  const handleSendNewsletter = async (newsletterId) => {
    if (!confirm("Are you sure you want to send this newsletter?")) return;
    
    setSending(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/newsletters/${newsletterId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send newsletter");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNewsletter = async (newsletterId) => {
    if (!confirm("Are you sure you want to delete this newsletter?")) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/newsletters/${newsletterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Newsletter deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete newsletter");
    }
  };

  // Subscriber handlers
  const handleAddSubscriber = async () => {
    if (!subscriberForm.email) {
      toast.error("Email is required");
      return;
    }
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/newsletter-subscribers`, subscriberForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscriber added");
      setSubscriberDialogOpen(false);
      setSubscriberForm({ email: "", name: "", tags: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add subscriber");
    }
  };

  const handleBulkUpload = async () => {
    if (!csvData.trim()) {
      toast.error("Please paste CSV data or upload a file");
      return;
    }
    
    setUploading(true);
    const token = localStorage.getItem("agent_token");
    
    try {
      const lines = csvData.trim().split('\n');
      const subscribersList = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const parts = line.includes('\t') ? line.split('\t') : line.split(',');
        const email = parts[0]?.trim();
        const name = parts[1]?.trim() || "";
        
        if (email && email.includes('@')) {
          subscribersList.push({ email, name });
        }
      }
      
      if (subscribersList.length === 0) {
        toast.error("No valid emails found in the data");
        setUploading(false);
        return;
      }
      
      const response = await axios.post(`${API}/agent/newsletter-subscribers/bulk-upload`, 
        { subscribers: subscribersList },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Added ${response.data.added} subscribers, skipped ${response.data.skipped} duplicates`);
      setUploadDialogOpen(false);
      setCsvData("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload subscribers");
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvData(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    if (!confirm("Delete this subscriber?")) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/newsletter-subscribers/${subscriberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscriber deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete subscriber");
    }
  };

  const addTagToForm = () => {
    if (newTag && !subscriberForm.tags.includes(newTag)) {
      setSubscriberForm({ ...subscriberForm, tags: [...subscriberForm.tags, newTag] });
      setNewTag("");
    }
  };

  const removeTagFromForm = (tag) => {
    setSubscriberForm({ ...subscriberForm, tags: subscriberForm.tags.filter(t => t !== tag) });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-100 text-gray-800",
      sending: "bg-blue-100 text-blue-800",
      sent: "bg-green-100 text-green-800"
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  const getAudienceName = (audienceId) => {
    const audience = audiences.find(a => a.id === audienceId);
    return audience?.name || audienceId;
  };

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
              <Mail className="inline h-8 w-8 mr-3 text-primary" />
              Email Marketing
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage newsletters and subscribers
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="newsletters" data-testid="newsletters-tab">Newsletters</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="subscribers-tab">
              Subscribers
              {subscriberStats?.non_customers > 0 && (
                <Badge className="ml-2 bg-blue-500 text-white text-xs">{subscriberStats.non_customers}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="audiences" data-testid="audiences-tab">Audiences</TabsTrigger>
          </TabsList>

          {/* Newsletters Tab */}
          <TabsContent value="newsletters" className="mt-6 space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setDialogOpen(true)} data-testid="create-newsletter-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Newsletter
              </Button>
            </div>

            {/* Newsletter Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="card-organic">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Newsletters</p>
                      <p className="text-2xl font-bold">{newsletters.length}</p>
                    </div>
                    <Mail className="h-8 w-8 text-primary opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-organic">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Sent</p>
                      <p className="text-2xl font-bold text-green-600">
                        {newsletters.filter(n => n.status === "sent").length}
                      </p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-organic">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Recipients</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {newsletters.reduce((sum, n) => sum + (n.sent_count || 0), 0)}
                      </p>
                    </div>
                    <Users className="h-8 w-8 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-organic">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Opens</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {newsletters.reduce((sum, n) => sum + (n.open_count || 0), 0)}
                      </p>
                    </div>
                    <Eye className="h-8 w-8 text-purple-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Newsletters Table */}
            <Card className="card-organic">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>All Newsletters</CardTitle>
                  <CardDescription>Manage your email campaigns</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : newsletters.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No newsletters yet. Create your first newsletter!
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Subject</th>
                          <th>Audience</th>
                          <th>Status</th>
                          <th>Sent</th>
                          <th>Opens</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newsletters.map(newsletter => (
                          <tr key={newsletter.newsletter_id}>
                            <td className="font-medium">{newsletter.subject}</td>
                            <td>{getAudienceName(newsletter.audience)}</td>
                            <td>{getStatusBadge(newsletter.status)}</td>
                            <td>{newsletter.sent_count || 0}</td>
                            <td>{newsletter.open_count || 0}</td>
                            <td>{new Date(newsletter.created_at).toLocaleDateString()}</td>
                            <td className="space-x-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  setSelectedNewsletter(newsletter);
                                  setPreviewOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {newsletter.status === "draft" && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleSendNewsletter(newsletter.newsletter_id)}
                                    disabled={sending}
                                    className="text-green-600 hover:bg-green-50"
                                  >
                                    <Send className="h-4 w-4 mr-1" />
                                    Send
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => handleDeleteNewsletter(newsletter.newsletter_id)}
                                    className="text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers" className="mt-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Non-Customer Subscribers</h2>
                <p className="text-sm text-muted-foreground">Manage email subscribers who are not registered customers</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setUploadDialogOpen(true)} data-testid="upload-csv-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload CSV
                </Button>
                <Button onClick={() => setSubscriberDialogOpen(true)} data-testid="add-subscriber-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Subscriber
                </Button>
              </div>
            </div>

            {/* Subscriber Stats */}
            {subscriberStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="card-organic">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-2xl font-bold">{subscriberStats.total}</p>
                  </CardContent>
                </Card>
                <Card className="card-organic bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-blue-700">Non-Customers</p>
                    <p className="text-2xl font-bold text-blue-800">{subscriberStats.non_customers}</p>
                  </CardContent>
                </Card>
                <Card className="card-organic bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-green-700">Customers</p>
                    <p className="text-2xl font-bold text-green-800">{subscriberStats.customers}</p>
                  </CardContent>
                </Card>
                <Card className="card-organic">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Subscribed</p>
                    <p className="text-2xl font-bold text-green-600">{subscriberStats.subscribed}</p>
                  </CardContent>
                </Card>
                <Card className="card-organic">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Unsubscribed</p>
                    <p className="text-2xl font-bold text-red-600">{subscriberStats.unsubscribed}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Top Tags */}
            {subscriberStats?.top_tags?.length > 0 && (
              <Card className="card-organic">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Popular Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {subscriberStats.top_tags.map(t => (
                      <Badge key={t.tag} variant="outline" className="px-3 py-1">
                        <Tag className="h-3 w-3 mr-1" />
                        {t.tag} ({t.count})
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Subscribers Table */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle>Subscriber List</CardTitle>
                <CardDescription>Email subscribers who haven't signed up as customers yet</CardDescription>
              </CardHeader>
              <CardContent>
                {subscribers.length === 0 ? (
                  <div className="text-center py-12">
                    <UserPlus className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No subscribers yet</h3>
                    <p className="text-muted-foreground mb-4">Add subscribers manually or upload a CSV file</p>
                    <div className="flex justify-center gap-2">
                      <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload CSV
                      </Button>
                      <Button onClick={() => setSubscriberDialogOpen(true)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Subscriber
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Tags</th>
                          <th>Status</th>
                          <th>Source</th>
                          <th>Added</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.map(sub => (
                          <tr key={sub.subscriber_id}>
                            <td className="font-medium">{sub.email}</td>
                            <td>{sub.name || "-"}</td>
                            <td>
                              <div className="flex flex-wrap gap-1">
                                {sub.tags?.length > 0 ? sub.tags.map(tag => (
                                  <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                                )) : <span className="text-muted-foreground text-xs">No tags</span>}
                              </div>
                            </td>
                            <td>
                              {sub.subscribed ? (
                                <Badge className="bg-green-100 text-green-800">Subscribed</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800">Unsubscribed</Badge>
                              )}
                            </td>
                            <td className="capitalize text-sm">{sub.source || "manual"}</td>
                            <td className="text-sm">{new Date(sub.created_at).toLocaleDateString()}</td>
                            <td>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDeleteSubscriber(sub.subscriber_id)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audiences Tab */}
          <TabsContent value="audiences" className="mt-6">
            <Card className="card-organic">
              <CardHeader>
                <CardTitle>Audience Segments</CardTitle>
                <CardDescription>Available target audiences for newsletters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Customer Audiences */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Registered Users
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {audiences.filter(a => a.type === "customers").map(audience => (
                      <div key={audience.id} className="p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                        <p className="text-sm font-medium">{audience.name}</p>
                        <p className="text-2xl font-bold text-primary">{audience.count}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Subscriber Audiences */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Newsletter Subscribers (Non-Customers)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {audiences.filter(a => a.type === "subscribers").map(audience => (
                      <div key={audience.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                        <p className="text-sm font-medium text-blue-800">{audience.name}</p>
                        <p className="text-2xl font-bold text-blue-600">{audience.count}</p>
                      </div>
                    ))}
                  </div>
                  {audiences.filter(a => a.type === "subscribers").length === 0 && (
                    <p className="text-muted-foreground text-sm">No non-customer subscribers yet. Add some in the Subscribers tab.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Newsletter Dialog with Rich Text Editor */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Newsletter</DialogTitle>
            <DialogDescription>Compose your newsletter with rich text formatting</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Enter newsletter subject"
                data-testid="newsletter-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Target Audience *</Label>
              <Select 
                value={form.audience} 
                onValueChange={(value) => setForm({ ...form, audience: value })}
              >
                <SelectTrigger data-testid="newsletter-audience-select">
                  <SelectValue placeholder="Select audience" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1 text-xs text-muted-foreground font-semibold bg-muted">Registered Users</div>
                  {audiences.filter(a => a.type === "customers").map(audience => (
                    <SelectItem key={audience.id} value={audience.id}>
                      {audience.name} ({audience.count})
                    </SelectItem>
                  ))}
                  {audiences.filter(a => a.type === "subscribers").length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs text-muted-foreground font-semibold bg-muted border-t">Non-Customer Subscribers</div>
                      {audiences.filter(a => a.type === "subscribers").map(audience => (
                        <SelectItem key={audience.id} value={audience.id}>
                          {audience.name} ({audience.count})
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Content *</Label>
              <div className="border rounded-lg overflow-hidden">
                <ReactQuill 
                  theme="snow"
                  value={form.content}
                  onChange={(content) => setForm({ ...form, content })}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your newsletter content here..."
                  style={{ minHeight: '300px' }}
                  data-testid="newsletter-content-editor"
                />
              </div>
              <p className="text-xs text-muted-foreground">Use the toolbar above to format your content with headings, colors, lists, and more.</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateNewsletter} data-testid="save-newsletter-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Newsletter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Newsletter Preview</DialogTitle>
          </DialogHeader>
          {selectedNewsletter && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{selectedNewsletter.subject}</h3>
                {getStatusBadge(selectedNewsletter.status)}
              </div>
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span>Audience: {getAudienceName(selectedNewsletter.audience)}</span>
                <span>Sent: {selectedNewsletter.sent_count || 0}</span>
                <span>Opens: {selectedNewsletter.open_count || 0}</span>
              </div>
              <div 
                className="border rounded-lg p-4 bg-white min-h-[200px] prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedNewsletter.content }}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            {selectedNewsletter?.status === "draft" && (
              <Button onClick={() => {
                handleSendNewsletter(selectedNewsletter.newsletter_id);
                setPreviewOpen(false);
              }}>
                <Send className="h-4 w-4 mr-2" />
                Send Newsletter
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subscriber Dialog */}
      <Dialog open={subscriberDialogOpen} onOpenChange={setSubscriberDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Subscriber</DialogTitle>
            <DialogDescription>Add a new email subscriber (non-customer)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={subscriberForm.email}
                onChange={(e) => setSubscriberForm({ ...subscriberForm, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="subscriber-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={subscriberForm.name}
                onChange={(e) => setSubscriberForm({ ...subscriberForm, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Tags (optional)</Label>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag (e.g., lead, prospect)"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTagToForm())}
                />
                <Button type="button" variant="outline" onClick={addTagToForm}>Add</Button>
              </div>
              {subscriberForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {subscriberForm.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer hover:bg-destructive hover:text-white" onClick={() => removeTagFromForm(tag)}>
                      {tag} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubscriberDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddSubscriber} data-testid="save-subscriber-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Subscriber
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload CSV Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Subscribers</DialogTitle>
            <DialogDescription>
              Upload a CSV file or paste email data to add multiple subscribers at once
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <input
                type="file"
                accept=".csv,.txt"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
                <Upload className="h-4 w-4 mr-2" />
                Choose CSV File
              </Button>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or paste data</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <textarea
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                placeholder="email@example.com,John Doe&#10;another@email.com,Jane Smith&#10;third@email.com"
                rows={8}
                className="w-full px-3 py-2 border rounded-md font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div className="bg-muted p-3 rounded-lg text-xs">
              <p className="font-medium mb-1">Supported formats:</p>
              <ul className="list-disc ml-4 space-y-0.5 text-muted-foreground">
                <li>email@example.com</li>
                <li>email@example.com,Name</li>
                <li>email@example.com	Name (tab separated)</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadDialogOpen(false); setCsvData(""); }}>
              Cancel
            </Button>
            <Button onClick={handleBulkUpload} disabled={uploading || !csvData.trim()}>
              {uploading ? "Uploading..." : "Upload Subscribers"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add custom styles for Quill editor */}
      <style>{`
        .ql-container {
          min-height: 250px;
          font-size: 14px;
        }
        .ql-editor {
          min-height: 250px;
        }
        .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          background: #f9fafb;
        }
        .ql-container {
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
      `}</style>
    </AgentLayout>
  );
}
